import React from 'react';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell } from '@/components/atoms';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import { COOLER_TRANSACTION_TEST_IDS as TID } from '@/constant/testIds/coolerTransaction.testIds';
import { tid } from '@/utils/testId';
import { ICoolerTransaction } from './coolerTransaction.interface';
import { COOLER_TRANSACTION_COLUMNS } from './constant';

/* ── Column definitions ──────────────────────────────────────────────────── */
export function buildColumns(
  isCopy: string,
  onCopyClick: (key: string) => void,
  providerList: any[],
): ColumnDef<ICoolerTransaction>[] {
  const getProviderName = (partnerUid: string | undefined) =>
    providerList?.find((p: any) => p?.partnerId === partnerUid)?.name ?? 'N/A';

  return [
    {
      accessor: 'transactionId',
      header: COOLER_TRANSACTION_COLUMNS.TRANSACTION_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_TRANSACTION_ID(`${idx}`))}
          value={item.transactionId}
          tooltip
          variant="mono"
          copy={{ copyKey: `txn-${item.transactionId}`, isCopy, onCopyClick }}
        />
      ),
    },
    {
      accessor: 'operatorId',
      header: COOLER_TRANSACTION_COLUMNS.OPERATOR_PROVIDER,
      sorting: true,
      render: (item, idx) => {
        const name = getProviderName(item.partnerUid);
        const isInvalid = name === 'N/A';
        return (
          <div
            className="flex flex-col gap-0.5"
            data-testid={tid(TID.COL_OPERATOR_PROVIDER(`${idx}`))}
          >
            <Cell
              value={name}
              className={`font-semibold max-w-[180px] ${isInvalid ? '!text-red-500 dark:!text-red-400' : ''}`}
            />
            <Cell
              value={item.operatorId}
              size="text-[11px]"
              className="max-w-[180px]"
            />
            <Cell
              value={item.partnerUid}
              size="text-[10px]"
              className="max-w-[180px]"
            />
          </div>
        );
      },
    },
    {
      accessor: 'marketId',
      header: COOLER_TRANSACTION_COLUMNS.MARKET,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_MARKET(`${idx}`))}
          value={item.marketId}
        />
      ),
    },
    {
      accessor: 'deviceId',
      header: COOLER_TRANSACTION_COLUMNS.DEVICE_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_DEVICE_ID(`${idx}`))}
          value={item.deviceId}
          className="max-w-[140px]"
        />
      ),
    },
    {
      accessor: 'type',
      header: COOLER_TRANSACTION_COLUMNS.TYPE,
      sorting: true,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_TYPE(`${idx}`))}
          value={item.type}
          preset="coolerType"
          shape="bar"
          dot
          className="font-bold tracking-wide"
        />
      ),
    },
    {
      accessor: 'status',
      header: COOLER_TRANSACTION_COLUMNS.STATUS,
      sorting: true,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS(`${idx}`))}
          value={item.status}
          preset="coolerStatus"
          dot
          className="font-bold border"
        />
      ),
    },
    {
      accessor: 'statusCode',
      header: COOLER_TRANSACTION_COLUMNS.STATUS_CODE,
      sorting: true,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_STATUS_CODE(`${idx}`))}
          value={item.statusCode}
          preset="statusCode"
          solid
          className="min-w-[3.5rem] justify-center tabular-nums font-mono"
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: COOLER_TRANSACTION_COLUMNS.LAST_UPDATED,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: COOLER_TRANSACTION_COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
        />
      ),
    },
  ];
}
